import React, { useState, useContext, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import * as Sentry from '@sentry/browser';
import AuthService from '../../../services/auth/AuthService';
import { Button as BaseButton } from '../../../components/buttons';
import FormController from '../../../components/globals/FormController';
import GlobalTheme from '../../../styled/GlobalTheme';
import StrokeBox from './StrokeBox';
import SignUpBox from './SignUpBox';
import LoginOptionsBox from './LoginOptionsBox';
import { AlertsContext } from '../../../contexts/AlertsContext';
import FacebookButton from '../../auth/components/FacebookButton';
import PasswordInput from '../../../components/globals/PasswordInput';
import { WebViewContext } from '../../../contexts/WebViewContext';
import AppleButton from '../../auth/components/AppleButton';
import { SEVERITY } from '../../../utils/constants/alerts';
import mediaQueries from '../../../utils/mediaQueries';

const Container = styled.div`
  display: flex;
  justify-content: center;
  background: ${({ theme, background }) => background || theme.colors.secondary};

  ${GlobalTheme.breakpoints.down('sm')} {
    padding: 0 24px;
  }
`;
const FormContainer = styled.div`
  max-width: 460px;
  width: 100%;
  z-index: 100;
`;

const ButtonContainer = styled.div`
  text-align: center;
  margin: 0 15%;
  ${({ isApp }) => isApp && `
    /* margin-top: 45px;
    margin-bottom: 77px; */
    margin-top: 36px;
    margin-bottom: 42px;
  `}
  ${GlobalTheme.breakpoints.up('md')} {
    margin: 0;
    ${({ isApp }) => isApp && `
      margin-top: 56px;
      margin-bottom: 40px;
    `}
  }
  @media ${mediaQueries.lgPlusUp} {
    ${({ isApp }) => isApp && `
      margin-top: 56px;
      margin-bottom: 50px;
    `}
  }
`;
const ButtonsWrapperSN = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;
const ButtonContainerSN = styled.div`
  text-align: center;
  margin: 0 18%;
  ${GlobalTheme.breakpoints.up('md')} {
    margin: 5% 20%;
    width: 60%;
    ${({ isApp }) => isApp && `
      margin: 0;
      width: auto;
      &:nth-child(1) {
        margin-right: 8px;
      }
    `}
  }
  ${({ isApp }) => isApp && `
    margin: 0;
    &:nth-child(1) {
      margin-right: 8px;
    }
  `}
`;

const Button = styled(BaseButton)`
  && {
    ${GlobalTheme.breakpoints.down('sm')} {
      font-size: 16px;
      width: 90%;
    }

    ${GlobalTheme.breakpoints.up('sm')} {
      width: 100%;
      ${({ isApp }) => isApp && `
        max-width: 220px;
      `}
    }
    ${({ isApp, theme }) => isApp && `
      background: ${theme.colors.secondary};
      box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.1);
      border-radius: 4px;
      color: ${theme.colors.grayMediaLibrary};
      &:hover {
        background: ${theme.colors.secondary};
      }
    `}
  }
`;

const Form = ({ setOpen, background, colorLabelInput }) => {
  const { showAlert, showError } = useContext(AlertsContext);
  const { isApp, isAppIOS } = useContext(WebViewContext);
  const intl = useIntl();
  const [user, setUserInfo] = useState({
    useremail: '',
    userpassword: '',
  });

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setUserInfo({
      ...user,
      [id]: value,
    });
  };

  const performLogin = async () => {
    try {
      await AuthService.login(user.useremail, user.userpassword, 'true');
    } catch (e) {
      Sentry.captureException(e);
      showError(
        intl.formatMessage({
          id: 'alert.error.loginEmail',
          defaultMessage:
            'Your mail or password is incorrect. Please write again.',
        }),
      );
    }
  };

  useEffect(() => {
    window.setPlayerID = (playerID) => {
      if (playerID) {
        window.localStorage.setItem('player_id', playerID);
      }
    };
  }, []);

  useEffect(() => {
    const existFlag = window.localStorage.getItem('registederuser');
    if (existFlag) {
      window.localStorage.removeItem('registederuser');
      showAlert(
        intl.formatMessage({
          id: 'k.login.message.userexist',
          defaultMessage: 'There is already an account with your email associated. Log in with that email.',
        }), SEVERITY.INFO,
      );
    }
  }, []);

  useEffect(() => {
    if (isApp) {
      setTimeout(() => {
        try {
          const playerID = window.Android.getPlayerID();
          if (playerID) {
            window.localStorage.setItem('player_id', playerID);
          }
          // eslint-disable-next-line no-empty
        } catch (error) { }
      }, 2000);
    }
  }, []);

  return (
    <Container background={background}>
      <FormContainer>
        <FormController
          id="useremail"
          onChange={handleInputChange}
          label="E-mail"
          value={user.useremail}
          colorLabelInput={colorLabelInput}
        />
        <PasswordInput
          id="userpassword"
          onChange={handleInputChange}
          label="Password"
          type="password"
          value={user.userpassword}
          colorLabelInput={colorLabelInput}
        />

        <LoginOptionsBox setOpen={setOpen} isApp={isApp || isAppIOS} />
        <ButtonContainer isApp={isApp || isAppIOS}>
          <Button
            size="l"
            onClick={() => performLogin()}
            isApp={isApp || isAppIOS}
          >
            <FormattedMessage id="k.login" defaultMessage="Log in" />
          </Button>
        </ButtonContainer>

        <StrokeBox isApp={isApp || isAppIOS} />
        {isApp ? (
          <ButtonsWrapperSN>
            <ButtonContainerSN isApp={isApp || isAppIOS}>
              <FacebookButton buttonType="loginApp" isApp={isApp || isAppIOS} isAndroid={isApp && !isAppIOS} />
            </ButtonContainerSN>

            <ButtonContainerSN isApp={isApp || isAppIOS}>
              <AppleButton wantsFree={false} buttonType="signin" />
            </ButtonContainerSN>
          </ButtonsWrapperSN>
        ) : (
          <>
            <ButtonContainerSN isApp={isApp || isAppIOS}>
              <FacebookButton buttonType="loginApp" />
            </ButtonContainerSN>

            <ButtonContainerSN isApp={isApp || isAppIOS}>
              <AppleButton wantsFree={false} buttonType="signin" />
            </ButtonContainerSN>
          </>
        )}
        <SignUpBox isAppIOS={isAppIOS} />
      </FormContainer>
    </Container>
  );
};

Form.propTypes = {
  setOpen: PropTypes.func.isRequired,
  background: PropTypes.string,
  colorLabelInput: PropTypes.string,
};

Form.defaultProps = {
  background: '',
  colorLabelInput: '',
};

export default Form;
